// src/lib/notebook/notebook.ts
//
// The mistake notebook: a student's own record of WHY they missed a question
// (one tap: misread / concept gap / calculation / guess) and the rule they want
// to remember. It is not a parallel store — an entry is a Bookmark row with a
// `missReason` and/or a `note` (the rule). Saving a reason bookmarks the
// question; the bookmark list and the notebook show the same rows.
//
// Distinct from Mistake DNA (src/lib/learning/mistake-dna.ts), which infers
// HOW a student misses from timing and answer data. This is the student's own
// call, and it is free: it's their data, not our analysis.
//
// Pure helpers only — shared by the route, the pages and the tests.

import type { Prisma } from '@prisma/client'

export const MISS_REASONS = ['MISREAD', 'CONCEPT_GAP', 'CALCULATION', 'GUESS'] as const
export type MissReason = (typeof MISS_REASONS)[number]

export const MISS_REASON_LABEL: Record<MissReason, string> = {
  MISREAD: 'Misread',
  CONCEPT_GAP: 'Concept gap',
  CALCULATION: 'Calculation',
  GUESS: 'Guess',
}

/** One line on what each reason means, for the button's title and the page. */
export const MISS_REASON_HINT: Record<MissReason, string> = {
  MISREAD: 'I misread the question or a choice',
  CONCEPT_GAP: "I didn't know the rule or idea it tests",
  CALCULATION: 'I knew how, but slipped on the working',
  GUESS: 'I guessed',
}

/** The rule-to-remember cap. Short on purpose: a rule, not an essay. */
export const RULE_MAX = 200

/** A notebook entry as the client sees it. `rule` is Bookmark.note. */
export type NotebookEntry = {
  missReason: MissReason | null
  rule: string | null
}

export function isMissReason(v: unknown): v is MissReason {
  return typeof v === 'string' && (MISS_REASONS as readonly string[]).includes(v)
}

/**
 * Read a request's `missReason`:
 *   undefined → not sent (leave the stored value alone)
 *   null      → clear it
 *   a reason  → set it
 * Anything else is invalid.
 */
export function parseMissReasonInput(
  v: unknown,
): { ok: true; value: MissReason | null | undefined } | { ok: false } {
  if (v === undefined) return { ok: true, value: undefined }
  if (v === null) return { ok: true, value: null }
  if (isMissReason(v)) return { ok: true, value: v }
  return { ok: false }
}

/**
 * Read a request's `rule`: undefined leaves it alone; null or blank clears it;
 * otherwise it is trimmed, inner whitespace runs collapsed, and capped at
 * RULE_MAX. A non-string is invalid.
 */
export function parseRuleInput(
  v: unknown,
): { ok: true; value: string | null | undefined } | { ok: false } {
  if (v === undefined) return { ok: true, value: undefined }
  if (v === null) return { ok: true, value: null }
  if (typeof v !== 'string') return { ok: false }
  const clean = v.replace(/\s+/g, ' ').trim().slice(0, RULE_MAX).trim()
  return { ok: true, value: clean.length > 0 ? clean : null }
}

/** A stored row → the entry the client sees (an unknown enum value reads as none). */
export function toNotebookEntry(row: {
  missReason?: string | null
  note?: string | null
}): NotebookEntry {
  return {
    missReason: isMissReason(row.missReason) ? row.missReason : null,
    rule: row.note && row.note.trim().length > 0 ? row.note : null,
  }
}

/**
 * An entry from an API payload ({ missReason, rule }), treated as untrusted:
 * null when it isn't an object, unknown reasons and non-string rules dropped.
 */
export function parseNotebookEntry(raw: unknown): NotebookEntry | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as { missReason?: unknown; rule?: unknown }
  return {
    missReason: isMissReason(r.missReason) ? r.missReason : null,
    rule: typeof r.rule === 'string' && r.rule.trim().length > 0 ? r.rule : null,
  }
}

/** Whether a bookmark is a notebook entry (has a reason or a rule). */
export function isNotebookEntry(entry: NotebookEntry): boolean {
  return entry.missReason !== null || entry.rule !== null
}

/** Prisma `where` for the bookmarks that are notebook entries. */
export const NOTEBOOK_ENTRY_WHERE: Prisma.BookmarkWhereInput = {
  OR: [{ missReason: { not: null } }, { note: { not: null } }],
}

// ---- The notebook page ----

export type NotebookReasonFilter = 'all' | MissReason | 'none'

export type NotebookRow = NotebookEntry & {
  questionId: string
  topic: string
  createdAt: string
}

/** Filter by reason ('none' = a rule but no reason) and topic ('all' = any). */
export function filterNotebook<T extends NotebookRow>(
  rows: T[],
  f: { reason: NotebookReasonFilter; topic: string },
): T[] {
  return rows.filter((r) => {
    if (f.reason === 'none' && r.missReason !== null) return false
    if (f.reason !== 'all' && f.reason !== 'none' && r.missReason !== f.reason) return false
    if (f.topic !== 'all' && r.topic !== f.topic) return false
    return true
  })
}

/** How many entries carry each reason, plus those with none — the filter chips. */
export function reasonCounts(rows: NotebookRow[]): Record<MissReason | 'none', number> {
  const counts: Record<MissReason | 'none', number> = {
    MISREAD: 0,
    CONCEPT_GAP: 0,
    CALCULATION: 0,
    GUESS: 0,
    none: 0,
  }
  for (const r of rows) counts[r.missReason ?? 'none'] += 1
  return counts
}

/** The practice session's ids-mode cap (/api/practice/start). */
export const NOTEBOOK_RETRY_MAX = 25

/** Retry exactly these questions, in order, returning to the notebook after. */
export function notebookRetryHref(questionIds: string[]): string {
  const ids = questionIds.slice(0, NOTEBOOK_RETRY_MAX)
  const label = ids.length === 1 ? 'Notebook question' : `${ids.length} notebook questions`
  const params = new URLSearchParams({ ids: ids.join(','), label, from: 'notebook' })
  return `/practice/session?${params.toString()}`
}

export const NOTEBOOK_HREF = '/dashboard/notebook'
